<!--
 * @Author: Christion.Young
 * @Date: 2025-07-30 10:00:00
 * @LastEditors: Christion.Young
 * @LastEditTime: 2025-07-30 10:00:00
 * @Description: 服务列表页面 - 红色主题，无移动端适配
 * @Features:
 *   - 服务筛选和搜索
 *   - 价格筛选
 *   - 分页显示
 *   - PC端专用设计
-->
<template>
  <div class="servicelist-page">
    <div class="servicelist-container">
      <div class="servicelist-header">
        <h1>{{ pageTitle }}</h1>
        <p v-if="!isSearchMode">发现优质服务，享受专业体验</p>
        <p v-else>为您找到以下相关服务</p>
      </div>

      <div class="servicelist-content">
        <!-- 搜索状态提示 -->
        <div v-if="isSearchMode" class="servicelist-search-status">
          <div class="search-info">
            <i class="el-icon-search"></i>
            <span
              >正在搜索：<strong>"{{ filterForm.keyword }}"</strong></span
            >
            <span class="search-results-count" v-if="!loading">
              (找到 {{ pagination.total }} 个相关服务)
            </span>
          </div>
          <button
            class="clear-search-btn"
            @click="clearSearch"
            title="清除搜索"
          >
            <i class="el-icon-close"></i>
            清除搜索
          </button>
        </div>

        <!-- 筛选条件 -->
        <div class="servicelist-filter-section">
          <form class="servicelist-filter-form" @submit.prevent="handleFilter">
            <div class="servicelist-form-group">
              <label>关键词搜索</label>
              <input
                v-model="filterForm.keyword"
                type="text"
                class="servicelist-form-input"
                placeholder="搜索服务名称"
                @keyup.enter="handleFilter"
                ref="keywordInput"
              />
            </div>
            <div class="servicelist-form-group">
              <label>服务分类</label>
              <select
                v-model="filterForm.serviceTypeId"
                class="servicelist-form-select"
                @change="handleFilter"
              >
                <option value="">全部分类</option>
                <option
                  v-for="category in serviceTypeList"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select>
            </div>
            <div class="servicelist-form-group">
              <label>排序方式</label>
              <select
                v-model="filterForm.sortBy"
                class="servicelist-form-select"
                @change="handleFilter"
              >
                <option
                  v-for="sort in sortOptions"
                  :key="sort.value"
                  :value="sort.value"
                >
                  {{ sort.label }}
                </option>
              </select>
            </div>
            <div class="servicelist-form-group servicelist-form-actions">
              <button
                type="submit"
                class="servicelist-btn servicelist-btn-primary"
              >
                搜索
              </button>
              <button
                type="button"
                class="servicelist-btn servicelist-btn-secondary"
                @click="resetFilter"
              >
                重置
              </button>
            </div>
          </form>
        </div>
        <!-- 服务列表 -->
        <div class="servicelist-list" :class="{ loading: loading }">
          <!-- 加载状态 -->
          <div v-if="loading" class="servicelist-loading-wrapper">
            <div class="servicelist-skeleton-grid">
              <div class="servicelist-skeleton-card" v-for="n in 9" :key="n">
                <div class="servicelist-skeleton-image"></div>
                <div class="skeleton-content">
                  <div class="servicelist-skeleton-line title"></div>
                  <div class="servicelist-skeleton-line desc"></div>
                  <div class="servicelist-skeleton-line desc"></div>
                  <div class="servicelist-skeleton-line price"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- 空状态 -->
          <div
            v-else-if="serviceList.length === 0"
            class="servicelist-empty-state"
          >
            <i class="el-icon-goods"></i>
            <p>暂无相关服务</p>
          </div>

          <!-- 服务网格 -->
          <div v-else class="servicelist-grid">
            <div
              v-for="service in serviceList"
              :key="service.id"
              class="servicelist-card"
              @click="viewServiceDetail(service)"
            >
              <div class="servicelist-image">
                <img :src="getServiceImage(service)" :alt="service.name" />
                <div class="servicelist-badges">
                  <span
                    v-if="service.isRecommended === '1'"
                    class="servicelist-badge servicelist-badge-recommend"
                  >
                    推荐
                  </span>
                  <span
                    v-if="service.isOnline === '1'"
                    class="servicelist-badge servicelist-badge-online"
                  >
                    在线
                  </span>
                  <span
                    class="servicelist-badge servicelist-badge-way"
                    :class="`servicelist-way-${service.serviceWay}`"
                  >
                    {{ getServiceWayText(service.serviceWay) }}
                  </span>
                </div>
              </div>
              <div class="servicelist-info">
                <h3 class="servicelist-title">{{ service.name }}</h3>
                <p class="servicelist-desc">
                  {{ getShortDesc(service.serviceDesc) }}
                </p>
                <div class="servicelist-merchant">
                  <i class="el-icon-office-building"></i>
                  <span>{{ service.merchantName }}</span>
                </div>
                <div class="servicelist-location" v-if="service.address">
                  <i class="el-icon-location"></i>
                  <span>{{ getShortAddress(service.address) }}</span>
                </div>
                <div class="servicelist-footer">
                  <div class="servicelist-price-section">
                    <span class="servicelist-price"
                      >¥{{ service.servicePrice }}</span
                    >
                    <span class="servicelist-price-unit">/次</span>
                  </div>
                  <div class="servicelist-stats">
                    <div class="servicelist-rating" v-if="service.evaluate">
                      <el-rate
                        :value="parseFloat(service.evaluate)"
                        disabled
                        show-score
                        text-color="#ff9900"
                        score-template="{value}"
                        :max="5"
                        allow-half
                      />
                    </div>
                    <div class="servicelist-sales" v-if="service.salesNumber">
                      <span>已售{{ service.salesNumber }}次</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="pagination.total > 0" class="servicelist-pagination-section">
          <div class="servicelist-pagination-info">
            共 {{ pagination.total }} 条记录
          </div>
          <div class="servicelist-pagination-controls">
            <button
              class="servicelist-page-btn"
              :disabled="pagination.currentPage === 1"
              @click="changePage(pagination.currentPage - 1)"
            >
              <i class="el-icon-arrow-left"></i>
              上一页
            </button>
            <div class="servicelist-page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                class="servicelist-page-number"
                :class="{ active: page === pagination.currentPage }"
                @click="changePage(page)"
                :disabled="typeof page === 'string'"
              >
                {{ page }}
              </button>
            </div>
            <button
              class="servicelist-page-btn"
              :disabled="pagination.currentPage === totalPages"
              @click="changePage(pagination.currentPage + 1)"
            >
              下一页
              <i class="el-icon-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 导入图片处理工具
import { getImageUrl } from "@/utils/imageUtils";
// 导入API
import { noPermissionRequired } from "@/api/index";

export default {
  name: "ServicesList",
  data() {
    return {
      loading: false,
      serviceList: [],
      filterForm: {
        // 新的排序值: "" | "price" | "sales" | "rating"
        sortBy: "",
        keyword: "", // 新增关键词搜索
        serviceTypeId: "", // 新增服务分类筛选
      },
      pagination: {
        currentPage: 1,
        pageSize: 50, // 修改为每页50条数据
        total: 0,
      },
      // 排序选项
      sortOptions: [
        { value: "", label: "默认" },
        { value: "price", label: "价格优先" },
        { value: "sales", label: "销量优先" },
        { value: "rating", label: "评分优先" },
      ],
      // 服务分类选项
      categoryOptions: [],
      // 服务分类列表（二级分类）
      serviceTypeList: [],
    };
  },
  computed: {
    /**
     * 是否为搜索模式
     */
    isSearchMode() {
      return !!this.filterForm.keyword.trim();
    },

    /**
     * 动态页面标题
     */
    pageTitle() {
      if (this.isSearchMode) {
        return `搜索"${this.filterForm.keyword}"的结果`;
      }
      return "服务列表";
    },

    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.pageSize);
    },
    visiblePages() {
      const current = this.pagination.currentPage;
      const total = this.totalPages;
      const visible = [];

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          visible.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            visible.push(i);
          }
          visible.push("...");
          visible.push(total);
        } else if (current >= total - 3) {
          visible.push(1);
          visible.push("...");
          for (let i = total - 4; i <= total; i++) {
            visible.push(i);
          }
        } else {
          visible.push(1);
          visible.push("...");
          for (let i = current - 1; i <= current + 1; i++) {
            visible.push(i);
          }
          visible.push("...");
          visible.push(total);
        }
      }

      return visible;
    },
  },
  created() {
    this.initFromQuery();
    this.loadServiceTypes();
    this.loadServices();
  },
  watch: {
    $route() {
      this.initFromQuery();
      this.loadServices();
    },
  },
  methods: {
    /**
     * 从URL参数初始化筛选条件
     */
    initFromQuery() {
      const query = this.$route.query;
      let qSort = query.sortBy || "";
      // 兼容旧的排序值，统一转换为新的枚举: "price" | "sales" | "rating" | ""
      switch (qSort) {
        case "price_asc":
          qSort = "price"; // 旧：价格从低到高
          break;
        case "price_desc":
          // 暂无倒序支持，归并为 price（如需严格区分可改成空字符串）
          qSort = "price";
          break;
        case "sales":
          qSort = "sales";
          break;
        case "rating":
          qSort = "rating";
          break;
        default:
          break;
      }
      if (!["", "price", "sales", "rating"].includes(qSort)) {
        qSort = ""; // 防御：未知值重置
      }
      this.filterForm.sortBy = qSort;
      this.filterForm.keyword = query.keyword || "";
      this.filterForm.serviceTypeId = query.serviceTypeId || "";
      this.pagination.currentPage = parseInt(query.page) || 1;

      console.log("服务列表页面接收到的筛选参数:", {
        serviceTypeId: query.serviceTypeId,
        sortBy: query.sortBy,
        keyword: query.keyword,
        page: query.page,
      });
    },

    /**
     * 加载服务分类列表
     */
    async loadServiceTypes() {
      try {
        const response = await noPermissionRequired.getServiceTypeDetail({
          current: 1,
          size: 999, // 获取所有二级分类
        });

        this.serviceTypeList = response?.data?.records || [];
        console.log("服务分类加载成功:", this.serviceTypeList);
      } catch (error) {
        console.error("加载服务分类失败:", error);
        this.serviceTypeList = [];
      }
    },

    /**
     * 加载服务列表
     */
    async loadServices() {
      this.loading = true;
      try {
        // 构建查询参数
        const params = {
          current: this.pagination.currentPage,
          size: this.pagination.pageSize,
        };

        // 添加筛选条件
        if (this.filterForm.keyword) {
          params.name = this.filterForm.keyword;
        }
        if (this.filterForm.sortBy) {
          // 互斥的排序参数映射，按后端要求只传其中一个
          switch (this.filterForm.sortBy) {
            case "price":
              params.pricePriority = "1"; // 价格从低到高
              break;
            case "sales":
              params.salesPriority = "1"; // 成交量高的服务排前
              break;
            case "rating":
              params.ratingPriority = "1"; // 评分高排前
              break;
            default:
              break; // 未知值则不传
          }
        }
        if (this.filterForm.serviceTypeId) {
          params.serviceTypeId = this.filterForm.serviceTypeId;
        }

        // 调用真实API（使用支持服务分类筛选的接口）
        const response = await noPermissionRequired.getServiceList(params);

        if (response && response.data) {
          this.serviceList = response.data.records || [];
          this.pagination.total = response.data.total || 0;
        } else {
          this.serviceList = [];
          this.pagination.total = 0;
        }
      } catch (error) {
        console.error("加载服务列表失败:", error);
        this.$msg.error("加载服务列表失败，请稍后重试");
        this.serviceList = [];
        this.pagination.total = 0;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 获取服务图片
     */
    getServiceImage(service) {
      if (service.avatarImg) {
        // 如果有头像图片，使用头像
        return getImageUrl(service.avatarImg);
      }
      if (service.imgList) {
        // 如果有图片列表，使用第一张
        const firstImg = service.imgList.split(",")[0];
        return getImageUrl(firstImg);
      }
      // 默认图片
      return require("@/assets/logo.png");
    },

    /**
     * 获取服务方式文本
     */
    getServiceWayText(serviceWay) {
      const wayMap = {
        1: "到店",
        2: "上门",
        3: "线上",
      };
      return wayMap[serviceWay] || "未知";
    },

    /**
     * 获取简短描述
     */
    getShortDesc(desc) {
      if (!desc) return "暂无描述";
      return desc.length > 80 ? desc.substring(0, 80) + "..." : desc;
    },

    /**
     * 获取简短地址
     */
    getShortAddress(address) {
      if (!address) return "";
      // 提取主要地址信息
      const parts = address.split(/[省市区县]/);
      return parts.length > 2 ? parts.slice(-2).join("") : address;
    },

    /**
     * 筛选处理
     */
    async handleFilter() {
      this.pagination.currentPage = 1;
      this.updateURL();
      await this.loadServices();
    },

    /**
     * 重置筛选
     */
    async resetFilter() {
      this.filterForm = {
        sortBy: "", // 清除排序
        keyword: "",
        serviceTypeId: "",
      };
      this.pagination.currentPage = 1;
      this.updateURL();
      await this.loadServices();
    },

    /**
     * 清除搜索
     */
    async clearSearch() {
      this.filterForm.keyword = "";
      this.pagination.currentPage = 1;
      this.updateURL();
      await this.loadServices();

      // 显示成功消息
      this.$msg.success("已清除搜索条件");
    },

    /**
     * 分页切换
     */
    async changePage(page) {
      if (
        page < 1 ||
        page > this.totalPages ||
        page === this.pagination.currentPage ||
        typeof page === "string"
      ) {
        return;
      }
      this.pagination.currentPage = page;
      this.updateURL();
      await this.loadServices();
    },

    /**
     * 更新URL参数
     */
    updateURL() {
      const query = {
        page: this.pagination.currentPage,
      };

      // 添加非空的筛选条件
      if (this.filterForm.sortBy) query.sortBy = this.filterForm.sortBy;
      if (this.filterForm.keyword) query.keyword = this.filterForm.keyword;
      if (this.filterForm.serviceTypeId)
        query.serviceTypeId = this.filterForm.serviceTypeId;

      // 移除空值
      Object.keys(query).forEach((key) => {
        if (!query[key] || query[key] === 1) {
          delete query[key];
        }
      });

      this.$router.replace({ query });
    },

    /**
     * 查看服务详情
     */
    viewServiceDetail(service) {
      if (!service?.id) {
        this.$msg.warning("服务信息不完整");
        return;
      }

      this.$router
        .push({
          name: "serviceDetail",
          params: { id: service.id },
        })
        .catch((err) => {
          console.error("跳转到服务详情失败:", err);
          this.$msg.error("跳转失败，请稍后重试");
        });
    },
  },
};
</script>

<style lang="scss" scoped>
// 红色主题色彩定义
$primary-red: #e74c3c;
$primary-red-dark: #c0392b;

.servicelist-page {
  min-height: 100vh;
  background: #f8f9fa;
  padding: 16px 0;

  // 防止外部样式污染
  * {
    box-sizing: border-box;
  }

  // 重置可能被污染的样式
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
    font-weight: normal;
  }

  p {
    margin: 0;
  }

  ul,
  ol {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  button {
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }
}

.servicelist-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

.servicelist-header {
  text-align: center;
  margin-bottom: 24px;
  padding: 20px 0;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  h1 {
    font-size: 24px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
    letter-spacing: -0.2px;
  }

  p {
    font-size: 14px;
    color: #7f8c8d;
    font-weight: 400;
  }
}

/* 搜索状态提示 */
.servicelist-search-status {
  background: linear-gradient(
    135deg,
    rgba(231, 76, 60, 0.1),
    rgba(231, 76, 60, 0.05)
  );
  border: 1px solid rgba(231, 76, 60, 0.2);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(231, 76, 60, 0.1);

  .search-info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #333;

    i {
      color: $primary-red;
      font-size: 16px;
    }

    strong {
      color: $primary-red;
      font-weight: 600;
    }

    .search-results-count {
      color: #666;
      font-size: 13px;
      margin-left: 8px;
    }
  }

  .clear-search-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background: white;
    border: 1px solid rgba(231, 76, 60, 0.3);
    border-radius: 6px;
    color: $primary-red;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(231, 76, 60, 0.1);
      border-color: $primary-red;
    }

    i {
      font-size: 12px;
    }
  }
}

/* 筛选区域 */
.servicelist-filter-section {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .servicelist-filter-form {
    display: flex;
    align-items: flex-end;
    gap: 16px;
    flex-wrap: wrap;

    .servicelist-form-group {
      display: flex;
      flex-direction: column;
      gap: 6px;

      label {
        font-size: 13px;
        font-weight: 600;
        color: #333;
      }

      .servicelist-form-input {
        min-width: 160px;
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 13px;
        background: white;
        transition: all 0.2s ease;

        &:focus {
          outline: none;
          border-color: $primary-red;
          box-shadow: 0 0 0 2px rgba(231, 76, 60, 0.1);
        }

        &:hover {
          border-color: #bbb;
        }

        &::placeholder {
          color: #999;
        }
      }

      .servicelist-form-select {
        min-width: 160px;
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 13px;
        background: white;
        transition: all 0.2s ease;

        &:focus {
          outline: none;
          border-color: $primary-red;
          box-shadow: 0 0 0 2px rgba(231, 76, 60, 0.1);
        }

        &:hover {
          border-color: #bbb;
        }
      }

      .servicelist-price-range {
        display: flex;
        align-items: center;
        gap: 8px;

        .servicelist-form-input {
          min-width: 80px;
        }

        .price-separator {
          color: #666;
          font-weight: 500;
        }
      }

      &.servicelist-form-actions {
        flex-direction: row;
        gap: 8px;
      }
    }

    .servicelist-btn {
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      border: 1px solid transparent;

      &.servicelist-btn-primary {
        background: $primary-red;
        color: white;
        border-color: $primary-red;

        &:hover {
          background: $primary-red-dark;
          border-color: $primary-red-dark;
        }
      }

      &.servicelist-btn-secondary {
        background: white;
        color: #666;
        border-color: #ddd;

        &:hover {
          background: #f8f9fa;
          border-color: #999;
        }
      }
    }
  }
}

/* 加载状态 */
.servicelist-loading-wrapper {
  .servicelist-skeleton-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;

    .servicelist-skeleton-card {
      background: white;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

      .servicelist-skeleton-image {
        width: 100%;
        height: 200px;
        background: #f0f0f0;
        border-radius: 0;
      }

      .skeleton-content {
        padding: 16px;

        .servicelist-skeleton-line {
          background: #f0f0f0;
          border-radius: 4px;
          margin-bottom: 8px;

          &.title {
            width: 90%;
            height: 18px;
          }

          &.desc {
            width: 100%;
            height: 14px;

            &:last-of-type {
              width: 80%;
            }
          }

          &.price {
            width: 70%;
            height: 16px;
            margin-bottom: 0;
          }
        }
      }
    }
  }
}

.servicelist-list {
  min-height: 300px;
}

.servicelist-empty-state {
  text-align: center;
  padding: 60px 16px;
  background: white;
  border-radius: 8px;
  color: #999;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);

  i {
    font-size: 48px;
    margin-bottom: 12px;
    display: block;
    color: $primary-red;
  }

  p {
    font-size: 14px;
  }
}

.servicelist-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  .servicelist-card {
    background: white;
    border: 1px solid #e8e8e8;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
      border-color: $primary-red;
      transform: translateY(-2px);
    }

    .servicelist-image {
      width: 100%;
      height: 500px;
      overflow: hidden;
      position: relative;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .servicelist-badges {
        position: absolute;
        top: 12px;
        left: 12px;
        display: flex;
        flex-direction: column;
        gap: 6px;

        .servicelist-badge {
          padding: 4px 8px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 600;
          color: white;
          text-align: center;
          min-width: 40px;
          backdrop-filter: blur(4px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

          &.servicelist-badge-recommend {
            background: linear-gradient(135deg, #ff6b6b, #ee5a24);
          }

          &.servicelist-badge-online {
            background: linear-gradient(135deg, #00d2d3, #54a0ff);
          }

          &.servicelist-badge-way {
            &.servicelist-way-1 {
              background: linear-gradient(135deg, #ffa726, #ff7043);
            }
            &.servicelist-way-2 {
              background: linear-gradient(135deg, #66bb6a, #43a047);
            }
            &.servicelist-way-3 {
              background: linear-gradient(135deg, #ab47bc, #8e24aa);
            }
          }
        }
      }
    }

    .servicelist-info {
      padding: 18px;

      .servicelist-title {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        margin-bottom: 10px;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      .servicelist-desc {
        font-size: 14px;
        color: #666;
        line-height: 1.5;
        margin-bottom: 16px;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .servicelist-merchant {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 8px;
        font-size: 13px;
        color: #7f8c8d;

        i {
          font-size: 14px;
          color: $primary-red;
          width: 14px;
          text-align: center;
        }
      }

      .servicelist-location {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 16px;
        font-size: 13px;
        color: #7f8c8d;

        i {
          font-size: 14px;
          color: #95a5a6;
          width: 14px;
          text-align: center;
        }
      }

      .servicelist-footer {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;

        .servicelist-price-section {
          display: flex;
          align-items: baseline;
          gap: 3px;

          .servicelist-price {
            font-size: 20px;
            font-weight: 700;
            color: $primary-red;
          }

          .servicelist-price-unit {
            font-size: 13px;
            color: #7f8c8d;
          }
        }

        .servicelist-stats {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 6px;

          .servicelist-rating {
            ::v-deep .el-rate {
              .el-rate__icon {
                font-size: 14px;
                margin-right: 2px;
              }
              .el-rate__text {
                font-size: 12px;
                color: #666;
                margin-left: 6px;
              }
            }
          }

          .servicelist-sales {
            font-size: 12px;
            color: #7f8c8d;
            background: #f8f9fa;
            padding: 3px 8px;
            border-radius: 6px;
            border: 1px solid #e9ecef;
          }
        }
      }
    }
  }
}

.servicelist-pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .servicelist-pagination-info {
    font-size: 14px;
    color: #7f8c8d;
  }

  .servicelist-pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;

    .servicelist-page-btn {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 8px 12px;
      background: white;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      color: #333;
      cursor: pointer;
      transition: all 0.3s ease;
      font-size: 13px;

      &:hover:not(:disabled) {
        border-color: $primary-red;
        color: $primary-red;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .servicelist-page-numbers {
      display: flex;
      gap: 4px;

      .servicelist-page-number {
        width: 32px;
        height: 32px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        background: white;
        color: #333;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;

        &:hover:not(:disabled) {
          border-color: $primary-red;
          color: $primary-red;
        }

        &.active {
          background: $primary-red;
          border-color: $primary-red;
          color: white;
        }

        &:disabled {
          cursor: default;
          opacity: 0.5;
        }
      }
    }
  }
}

/* Element UI 组件样式保护 */
.servicelist-page {
  // 评分组件样式保护
  ::v-deep .el-rate {
    height: auto !important;
    line-height: normal !important;

    .el-rate__item {
      font-size: 14px !important;
      margin-right: 2px !important;
    }

    .el-rate__text {
      font-size: 12px !important;
      color: #666 !important;
      margin-left: 6px !important;
    }
  }

  // 按钮样式保护
  ::v-deep .el-button {
    font-family: inherit !important;
    border-radius: 4px !important;
  }

  // 选择框样式保护
  ::v-deep .el-select {
    .el-input__inner {
      border-radius: 4px !important;
      border-color: #dcdfe6 !important;

      &:focus {
        border-color: $primary-red !important;
      }
    }
  }
}
</style>
